import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  IconChevronRight, IconCopy, IconGift, IconDiamond,
} from '@tabler/icons-react';
import RU from 'country-flag-icons/react/3x2/RU';
import GB from 'country-flag-icons/react/3x2/GB';
import ES from 'country-flag-icons/react/3x2/ES';
import DE from 'country-flag-icons/react/3x2/DE';
import FR from 'country-flag-icons/react/3x2/FR';
import { useTonConnectUI } from '@tonconnect/ui-react';
import { Button } from '@/components/Button';
import { Skeleton } from '@/components/Skeleton';
import { Modal } from '@/components/Modal';
import { Avatar, displayName } from '@/components/Avatar';
import { api } from '@/api/endpoints';
import type { CardGateway, ReferralsResponse, ShelfVisibility } from '@/api/types';
import { useAuth } from '@/store/auth';
import { useUI, TON_USD_RATE, type CurrencyMode } from '@/store/ui';
import { formatTon } from '@/lib/format';
import { haptic } from '@/lib/telegram';
import { useTonWalletSync } from '@/lib/useTonWalletSync';
import { SupportModal } from '@/features/support/SupportModal';

const LANGS = [
  { code: 'ru', label: 'Русский', Flag: RU },
  { code: 'en', label: 'English', Flag: GB },
  { code: 'es', label: 'Español', Flag: ES },
  { code: 'de', label: 'Deutsch', Flag: DE },
  { code: 'fr', label: 'Français', Flag: FR },
] as const;

const NOTIF_KEYS = [
  'comment_like',
  'comment_reply',
  'streak_milestone',
  'subscription_activated',
  'subscription_ending',
  'referral_joined',
  'referral_bonus',
  'daily_reminder',
  'book_finished',
  'friend_shelved_book',
] as const;
type NotifKey = typeof NOTIF_KEYS[number];

const NOTIF_LABELS: Record<NotifKey, { titleKey: string; subKey: string }> = Object.fromEntries(
  NOTIF_KEYS.map(k => [k, { titleKey: `profile.notif.${k}.title`, subKey: `profile.notif.${k}.sub` }])
) as Record<NotifKey, { titleKey: string; subKey: string }>;

function defaultNotifPrefs(): Record<NotifKey, boolean> {
  return Object.fromEntries(NOTIF_KEYS.map(k => [k, true])) as Record<NotifKey, boolean>;
}

export default function Profile() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const user = useAuth(s => s.user);
  const patchUser = useAuth(s => s.patchUser);
  const logout = useAuth(s => s.logout);
  const showToast = useUI(s => s.showToast);
  const currencyMode = useUI(s => s.currencyMode);
  const setCurrencyMode = useUI(s => s.setCurrencyMode);
  const [tonConnectUI] = useTonConnectUI();
  useTonWalletSync();

  const [stats, setStats] = useState<{ cards_read_total: number; bookmarks_total: number; streak_days: number; books_opened: number } | null>(null);
  const [referrals, setReferrals] = useState<Pick<ReferralsResponse, 'invited_count' | 'total_earned_ton'> | null>(null);
  const [openModal, setOpenModal] = useState<'lang' | 'notif' | 'support' | 'topup_card' | 'visibility' | null>(null);
  const [notifPrefs, setNotifPrefs] = useState<Record<NotifKey, boolean>>(defaultNotifPrefs);

  useEffect(() => {
    void api.me.notificationPrefs.get().then(({ data }) => {
      if (data) setNotifPrefs(prev => ({ ...prev, ...data }));
    });
  }, []);
  const { i18n } = useTranslation();

  useEffect(() => { void api.me.stats().then(({ data }) => setStats(data)); }, []);
  useEffect(() => {
    void api.me.referrals().then(({ data }) => {
      if (data) setReferrals({ invited_count: data.invited_count, total_earned_ton: data.total_earned_ton });
    });
  }, []);

  const setShelfVisibility = async (v: ShelfVisibility) => {
    haptic.light();
    try {
      await api.me.setShelfVisibility(v);
      patchUser({ shelf_visibility: v });
    } catch {
      showToast(t('errors.generic'), 'error');
    }
  };

  const pickLang = async (lang: 'ru' | 'en' | 'es' | 'de' | 'fr') => {
    try {
      await api.me.setLang(lang);
      patchUser({ lang });
      void i18n.changeLanguage(lang);
      setOpenModal(null);
    } catch {
      showToast(t('profile.lang_change_error'), 'error');
    }
  };

  const toggleNotif = (key: NotifKey) => {
    const next = { ...notifPrefs, [key]: !notifPrefs[key] };
    setNotifPrefs(next); // optimistic
    void api.me.notificationPrefs
      .patch({ [key]: next[key] })
      .then(({ data }) => { if (data) setNotifPrefs(prev => ({ ...prev, ...data })); })
      .catch(() => { setNotifPrefs(notifPrefs); /* rollback */ });
  };

  const switchCurrency = (next: CurrencyMode) => {
    if (next === currencyMode) return;
    haptic.light();
    setCurrencyMode(next);
  };

  const onTopupClick = () => {
    haptic.medium();
    if (currencyMode === 'card') setOpenModal('topup_card');
    else nav('/topup');
  };

  const pickCardGateway = async (gateway: CardGateway) => {
    haptic.medium();
    try { await api.paymentIntent.create('card', gateway); } catch { /* ignore */ }
    setOpenModal(null);
    showToast(t('subscription.card_coming_soon'), 'success');
  };

  const connectWallet = () => { void tonConnectUI.openModal(); };
  const disconnectWallet = async () => {
    try { await tonConnectUI.disconnect(); } catch { /* ignore */ }
    try { await api.wallet.disconnect(); } catch { /* ignore */ }
    patchUser({ ton_wallet: null });
  };

  if (!user) return <div className="p-4"><Skeleton height={120} /></div>;

  const copyRef = async () => {
    try {
      await navigator.clipboard.writeText(user.ref_link);
      showToast(t('common.copied'), 'success');
    } catch {
      showToast(t('errors.generic'), 'error');
    }
  };

  return (
    <div className="flex-1 overflow-y-auto bg-bg">
      {/* Шапка */}
      <header className="px-4 pt-5 pb-3 flex items-center gap-3">
        <Avatar user={user} size="lg" />
        <div className="flex-1 min-w-0">
          <div className="text-base font-medium truncate">{displayName(user)}</div>
          {user.username && (
            <div className="text-xs text-text-muted truncate">@{user.username}</div>
          )}
        </div>
      </header>

      <div className="px-4 space-y-3 pb-8">
        {/* Баланс */}
        <section className="rounded-xl p-4 bg-balance text-text shadow">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[11px] tracking-widest font-semibold opacity-70">{t('profile.balance')}</p>
              <p className="text-[10px] opacity-50 mt-0.5">{t('profile.balance_sub')}</p>
            </div>
            <div className="flex gap-1 p-1 bg-white/40 rounded-pill shrink-0">
              {(['crypto', 'card'] as const).map(m => (
                <button
                  key={m}
                  onClick={() => switchCurrency(m)}
                  className={[
                    'h-7 px-3 rounded-pill text-[11px] font-medium transition',
                    currencyMode === m ? 'bg-white text-text shadow-sm' : 'text-text/70',
                  ].join(' ')}
                >
                  {t(m === 'crypto' ? 'profile.currency_crypto' : 'profile.currency_card')}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            {currencyMode === 'card' ? (
              <>
                <span className="text-3xl font-medium">${(user.balance_ton * TON_USD_RATE).toFixed(0)}</span>
                <span className="text-xs opacity-70">≈ {formatTon(user.balance_ton)} TON</span>
              </>
            ) : (
              <>
                <span className="text-3xl font-medium">{formatTon(user.balance_ton)} TON</span>
                <span className="text-xs opacity-70">≈ ${(user.balance_ton * TON_USD_RATE).toFixed(0)}</span>
              </>
            )}
          </div>
          <div className="mt-3 flex gap-2">
            {currencyMode === 'crypto' && (
              <Button size="sm" block variant="secondary" onClick={() => nav('/withdrawal')}>{t('profile.withdraw')}</Button>
            )}
            <Button size="sm" block variant="secondary" onClick={onTopupClick}>{t('profile.topup')}</Button>
          </div>
        </section>

        {/* Кошелёк */}
        <section className="rounded-xl bg-surface p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-pill grid place-items-center bg-ton text-white text-xs font-bold">TON</div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium">{user.ton_wallet ? t('profile.wallet_connected') : t('profile.wallet_not_connected')}</div>
            {user.ton_wallet && <div className="text-xs text-text-muted truncate">{shorten(user.ton_wallet)}</div>}
          </div>
          {user.ton_wallet ? (
            <button className="text-xs text-danger" onClick={disconnectWallet}>
              {t('profile.disconnect')}
            </button>
          ) : (
            <button className="text-xs font-medium text-accent" onClick={connectWallet}>
              {t('profile.connect')}
            </button>
          )}
        </section>

        {/* Подписка */}
        <section className="rounded-xl p-4 bg-dark text-text-inverse">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-pill bg-white/15 grid place-items-center shrink-0">
                <IconDiamond size={20} />
              </div>
              <div className="min-w-0">
                <div className="font-medium text-sm truncate">
                  {user.subscription_active && user.subscription_plan
                    ? t(`profile.sub_active_${user.subscription_plan}`, {
                        date: user.subscription_expires_at
                          ? new Date(user.subscription_expires_at).toLocaleDateString(
                              i18n.language || 'en',
                              { day: '2-digit', month: '2-digit', year: 'numeric' },
                            )
                          : '—',
                      })
                    : t('profile.no_subscription')}
                </div>
                {!user.subscription_active && <div className="text-xs opacity-70 mt-1">{t('profile.ads_each_n')}</div>}
              </div>
            </div>
            <Button size="sm" variant="secondary" onClick={() => nav('/subscription')}>
              {user.subscription_active
                ? t('profile.sub_manage', { defaultValue: 'Управлять' })
                : t('profile.connect')}
            </Button>
          </div>
        </section>

        {/* Статистика 2x2 */}
        <section className="grid grid-cols-2 gap-3">
          <Stat title={t('profile.stat_read')} value={stats?.cards_read_total ?? user.cards_read_total} onClick={() => nav('/profile/history')} />
          <Stat title={t('profile.stat_shelf')} value={stats?.bookmarks_total ?? 0} onClick={() => nav('/shelf')} />
          <Stat title={t('profile.stat_streak')} value={stats?.streak_days ?? user.streak_days} suffix="🔥" onClick={() => nav('/profile/streak')} />
          <Stat title={t('profile.stat_books')} value={stats?.books_opened ?? 0} onClick={() => nav('/profile/books')} />
        </section>

        {/* Реферальная программа */}
        <section className="rounded-xl bg-surface p-4 space-y-3">
          <div className="flex items-center gap-2">
            <IconGift size={20} className="text-text-muted" />
            <div>
              <div className="text-sm font-medium">{t('profile.ref_title')}</div>
              <div className="text-xs text-text-muted">{t('profile.ref_sub', { p: 20 })}</div>
            </div>
          </div>
          <button onClick={copyRef} className="w-full bg-white/70 rounded-md p-3 flex items-center justify-between text-xs">
            <span className="truncate text-text-muted">{user.ref_link}</span>
            <IconCopy size={16} />
          </button>
          <div className="text-xs text-text-muted">
            {t('profile.ref_invited', { n: referrals?.invited_count ?? 0 })} · {t('profile.ref_earned', { ton: formatTon(referrals?.total_earned_ton ?? 0) })}
          </div>
        </section>

        {/* Меню */}
        <section className="rounded-xl bg-surface divide-y divide-text/10">
          <MenuRow label={t('profile.menu_topics')} hint={t('topics.topics_count_hint', { n: user.topics?.length ?? 0 })} onClick={() => nav('/topics')} />
          <MenuRow label={t('profile.menu_visibility')} hint={t(`shelf.visibility_${user.shelf_visibility}`, { defaultValue: t('shelf.visibility_all') })} onClick={() => setOpenModal('visibility')} />
          <MenuRow label={t('profile.menu_lang')} hint={user.lang.toUpperCase()} onClick={() => setOpenModal('lang')} />
          <MenuRow label={t('profile.menu_notifications')} onClick={() => setOpenModal('notif')} />
          <MenuRow label={t('profile.menu_support')} onClick={() => setOpenModal('support')} />
          <MenuRow label={t('profile.menu_logout')} dangerous onClick={() => void logout()} />
        </section>
      </div>

      {/* Видимость полки */}
      <Modal
        open={openModal === 'visibility'}
        onClose={() => setOpenModal(null)}
        title={t('shelf.settings_title', { defaultValue: 'Настройки полки' })}
        height={240}
      >
        <div className="px-4 py-3 space-y-2">
          {([
            { value: 'all',     label: t('shelf.visibility_all') },
            { value: 'friends', label: t('shelf.visibility_friends') },
            { value: 'hidden',  label: t('shelf.visibility_hidden') },
          ] as { value: ShelfVisibility; label: string }[]).map(opt => (
            <button
              key={opt.value}
              onClick={() => void setShelfVisibility(opt.value)}
              className={`w-full flex items-center justify-between px-3 py-3 rounded-xl border transition ${
                user.shelf_visibility === opt.value
                  ? 'bg-accent/10 border-accent text-text'
                  : 'bg-surface border-transparent text-text-muted'
              }`}
            >
              <span className="text-sm font-medium">{opt.label}</span>
              <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                user.shelf_visibility === opt.value ? 'border-accent' : 'border-text-disabled'
              }`}>
                {user.shelf_visibility === opt.value && (
                  <span className="w-2 h-2 rounded-full bg-accent" />
                )}
              </span>
            </button>
          ))}
        </div>
      </Modal>

      {/* Язык */}
      <Modal open={openModal === 'lang'} onClose={() => setOpenModal(null)} title={t('profile.lang_modal_title')} height={360}>
        <div className="p-2">
          {LANGS.map(l => (
            <button
              key={l.code}
              onClick={() => void pickLang(l.code)}
              className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left hover:bg-surface ${user.lang === l.code ? 'bg-surface' : ''}`}
            >
              <l.Flag title={l.label} className="w-6 h-4 rounded-sm shrink-0" />
              <span className="flex-1 text-sm">{l.label}</span>
              {user.lang === l.code && <span className="text-accent text-sm">✓</span>}
            </button>
          ))}
        </div>
      </Modal>

      {/* Уведомления */}
      <Modal open={openModal === 'notif'} onClose={() => setOpenModal(null)} title={t('profile.menu_notifications')} height={600}>
        <div className="px-4 py-3 space-y-3">
          {NOTIF_KEYS.map(key => (
            <label key={key} className="flex items-center justify-between gap-3 py-2">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{t(NOTIF_LABELS[key].titleKey)}</div>
                <div className="text-xs text-text-muted">{t(NOTIF_LABELS[key].subKey)}</div>
              </div>
              <input
                type="checkbox"
                className="h-6 w-10 appearance-none rounded-pill bg-surface checked:bg-accent transition-colors relative
                           before:absolute before:top-0.5 before:left-0.5 before:h-5 before:w-5 before:rounded-full before:bg-white before:transition-transform
                           checked:before:translate-x-4"
                checked={notifPrefs[key]}
                onChange={() => toggleNotif(key)}
              />
            </label>
          ))}
        </div>
      </Modal>

      {/* Поддержка */}
      <SupportModal open={openModal === 'support'} onClose={() => setOpenModal(null)} />

      {/* Пополнение картой — выбор страны */}
      <Modal
        open={openModal === 'topup_card'}
        onClose={() => setOpenModal(null)}
        title={t('profile.topup_modal_title')}
        height={280}
      >
        <div className="p-4 space-y-3">
          <p className="text-sm text-text-muted">{t('profile.topup_modal_sub')}</p>
          <Button block size="lg" onClick={() => void pickCardGateway('russian')}>
            {t('profile.topup_russian_card')}
          </Button>
          <Button block size="lg" variant="secondary" onClick={() => void pickCardGateway('international')}>
            {t('profile.topup_international_card')}
          </Button>
        </div>
      </Modal>
    </div>
  );
}

function Stat({ title, value, suffix, onClick }: { title: string; value: number; suffix?: string; onClick?: () => void }) {
  const cls = 'rounded-xl bg-surface p-4 text-left w-full';
  const inner = (
    <>
      <div className="text-xs text-text-muted">{title}</div>
      <div className="mt-1 text-2xl font-medium">
        {value}{suffix && <span className="text-base ml-1">{suffix}</span>}
      </div>
    </>
  );
  return onClick
    ? <button type="button" onClick={onClick} className={cls + ' active:opacity-70 transition-opacity'}>{inner}</button>
    : <div className={cls}>{inner}</div>;
}

function MenuRow({ label, hint, onClick, dangerous }: { label: string; hint?: string; onClick?: () => void; dangerous?: boolean }) {
  return (
    <button onClick={onClick} className={`w-full flex items-center justify-between gap-3 px-4 py-3 text-sm ${dangerous ? 'text-danger' : 'text-text'}`}>
      <span>{label}</span>
      <span className="flex items-center gap-2 text-xs text-text-muted">
        {hint}
        <IconChevronRight size={14} />
      </span>
    </button>
  );
}

function shorten(addr: string) {
  if (addr.length < 12) return addr;
  return `${addr.slice(0, 4)}…${addr.slice(-4)}`;
}
